import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import CardPicker from '../components/CardPicker';
import Header from '../components/Header';
import InviteModal from '../components/InviteModal';
import JoinModal from '../components/JoinModal';
import PlayerList from '../components/PlayerList';
import SettingsModal from '../components/SettingsModal';
import VoteStatus from '../components/VoteStatus';
import { useGame } from '../contexts/GameContext';
import { playCue } from '../lib/foley';
import { useGameSounds } from '../lib/useGameSounds';
import './game.css';

const GameSession: React.FC = () => {
  const { gameId: routeGameId } = useParams<{ gameId: string }>();
  const {
    setGameId,
    players,
    currentPlayer,
    currentPlayerId,
    cardOptions,
    cardSet,
    votesRevealed,
    endVotingRequestCount,
    hasRequestedEndVoting,
    connectionStatus,
    joinGame,
    castVote,
    resetGame,
    changeCardSet,
    toggleEndVoting,
  } = useGame();

  const [isResettingRound, setIsResettingRound] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  useGameSounds();

  useEffect(() => {
    if (routeGameId) {
      setGameId(routeGameId);
    }
  }, [routeGameId, setGameId]);

  // Re-enable the reset button when votes are no longer revealed
  useEffect(() => {
    if (!votesRevealed && isResettingRound) {
      setIsResettingRound(false);
    }
  }, [votesRevealed, isResettingRound]);

  const votesCasted = players.filter((player) => player.hasVoted).length;
  const handleSelectCard = (value: string | number) => {
    castVote(String(value));
  };

  const showReset = votesRevealed && players.length > 0;
  const showEndVoting = !votesRevealed && players.length >= 2 && votesCasted >= 1;

  const handleResetRound = () => {
    console.info('[GameSession] handleResetRound clicked', { isResettingRound });
    if (isResettingRound) {
      return;
    }

    setIsResettingRound(true);
    console.info('[GameSession] calling resetGame()');
    resetGame();
  };

  // Sounded here rather than with data-foley-toggle, which reads aria-pressed
  // before React has re-rendered it and would announce the state we just left.
  const handleToggleEndVoting = () => {
    playCue(hasRequestedEndVoting ? 'off' : 'on');
    toggleEndVoting();
  };

  return (
    <>
      <Header
        gameControls={
          <>
            <button
              type="button"
              className="app-header__link"
              onClick={() => setIsInviteOpen(true)}
              aria-label="Invite others"
              data-foley-click="swoosh"
            >
              <span>👥</span>
              <span>Invite Others</span>
            </button>
            <button
              type="button"
              className="app-header__link app-header__link--icon"
              onClick={() => setIsSettingsOpen(true)}
              aria-label="Open settings"
              data-foley-click="swoosh"
            >
              ⚙️
            </button>
          </>
        }
      />
      <div className="game-session">
        <div className="game-session__table-shell">
        <div className="game-session__table" role="group" aria-label="Planning poker table">
          <div className="game-session__felt">
            <VoteStatus totalPlayers={players.length} votesCasted={votesCasted} votesRevealed={votesRevealed} />
            {showEndVoting && (
              <button
                type="button"
                className={`end-voting-button${hasRequestedEndVoting ? ' end-voting-button--requested' : ''}`}
                onClick={handleToggleEndVoting}
                aria-pressed={hasRequestedEndVoting}
              >
                {hasRequestedEndVoting
                  ? `Cancel End Voting (${endVotingRequestCount}/2)`
                  : `End Voting for All (${endVotingRequestCount}/2)`}
              </button>
            )}
            {showReset && (
              <button
                type="button"
                className={`reset-button${isResettingRound ? ' reset-button--active' : ''}`}
                onClick={handleResetRound}
                disabled={isResettingRound}
                data-foley-press="press"
              >
                Start a new round
              </button>
            )}
          </div>
          <PlayerList players={players} isRevealed={votesRevealed} currentPlayerId={currentPlayerId} />
        </div>
      </div>
      {currentPlayer && (
        <CardPicker
          options={cardOptions}
          onSelectCard={handleSelectCard}
          selectedCard={currentPlayer.vote}
          hasLockedIn={currentPlayer.hasVoted}
          isRevealed={votesRevealed}
        />
      )}
      <JoinModal isOpen={!currentPlayer} onJoin={joinGame} connectionStatus={connectionStatus} />
      <InviteModal isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)} />
      <SettingsModal
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        currentCardSet={cardSet}
        onChangeCardSet={changeCardSet}
      />
      </div>
    </>
  );
};

export default GameSession;
